import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Upload, X, Loader2 } from "lucide-react";

const CATEGORIES = ["Electronics", "Fashion", "Home", "Vehicles", "Collectibles", "Services", "Sports", "Art", "Other"];

export default function CreateListing() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", category: "Electronics", type: "good", location: "", wanted: "", value_usd: "" });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.url]);
      } catch (err) {
        toast.error("Upload failed");
      }
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!form.title || !form.description || !form.wanted || !form.value_usd) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/listings", { ...form, value_usd: parseFloat(form.value_usd), images });
      toast.success("Listing published!");
      navigate(`/listing/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create listing");
    }
    setSubmitting(false);
  };

  const inputCls = "w-full mt-1 p-3 nb-border rounded-xl font-medium outline-none focus:ring-2 focus:ring-[#E6B31E]";

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-8">List an item or service</h1>

      <div className="bg-white nb-border rounded-2xl p-6 sm:p-8 nb-shadow space-y-5">
        <div className="flex gap-2">
          {[{ v: "good", l: "A Good" }, { v: "service", l: "A Service" }].map((t) => (
            <button key={t.v} data-testid={`type-${t.v}`} onClick={() => setForm({ ...form, type: t.v })}
              className={`flex-1 font-extrabold rounded-xl py-3 border-2 border-black transition-colors ${form.type === t.v ? "bg-[#E6B31E] text-black" : "bg-white hover:bg-[#FBF7E9]"}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div>
          <label className="text-sm font-bold">Title *</label>
          <input data-testid="title-input" value={form.title} onChange={set("title")} placeholder="Vintage film camera" className={inputCls} />
        </div>

        <div>
          <label className="text-sm font-bold">Description *</label>
          <textarea data-testid="description-input" value={form.description} onChange={set("description")} rows={4} placeholder="Condition, details, anything relevant…" className={inputCls} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-bold">Category</label>
            <select data-testid="category-select" value={form.category} onChange={set("category")} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-bold">Est. value (USD) *</label>
            <input data-testid="value-input" type="number" value={form.value_usd} onChange={set("value_usd")} placeholder="300" className={inputCls} />
          </div>
        </div>

        <div>
          <label className="text-sm font-bold">What do you want in exchange? *</label>
          <input data-testid="wanted-input" value={form.wanted} onChange={set("wanted")} placeholder="A mountain bike, or web design work…" className={inputCls} />
        </div>

        <div>
          <label className="text-sm font-bold">Location (optional)</label>
          <input data-testid="location-input" value={form.location} onChange={set("location")} placeholder="Cape Town" className={inputCls} />
        </div>

        <div>
          <label className="text-sm font-bold">Photos</label>
          <div className="mt-1 flex flex-wrap gap-3">
            {images.map((img, i) => (
              <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden nb-border">
                <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setImages(images.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black text-white rounded-full p-0.5">
                  <X size={14} strokeWidth={3} />
                </button>
              </div>
            ))}
            <label data-testid="upload-btn" className="w-24 h-24 rounded-lg nb-border border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-[#FBF7E9] text-black/50">
              {uploading ? <Loader2 className="animate-spin" size={22} /> : <Upload size={22} strokeWidth={2.5} />}
              <span className="text-[10px] font-bold mt-1">Add photo</span>
              <input type="file" accept="image/*" multiple hidden onChange={handleUpload} />
            </label>
          </div>
        </div>

        <div className="bg-black text-white rounded-xl px-4 py-3 text-sm font-semibold">
          <span className="text-[#E6B31E]">Zero fees</span> — Tradeable Exchange charges no commission on trades.
        </div>

        <button data-testid="publish-btn" disabled={submitting} onClick={submit}
          className="w-full bg-[#E6B31E] text-black font-extrabold text-lg rounded-full px-6 py-4 border-2 border-black nb-shadow nb-hover disabled:opacity-50">
          {submitting ? "Publishing…" : "Publish listing"}
        </button>
      </div>
    </div>
  );
}
